// Surface detection for the MCP App layout. The MCP App shell (app/mcp-app/
// shell.ts) mounts play.pixmoto.com/{id} in the host card with
// ?pix_surface=mcp-app&pix_host=<slug>. This reader turns that into a layout
// decision (Experience.js). Mirrors mcpAppLink.ts's param reading; kept pure so
// it is unit-testable. Here we only detect the param the shell already sends.

const MCP_APP_SURFACE = "mcp-app";
const MCP_LAYOUT_CLASSIC = "classic";
const KNOWN_HOSTS = ["chatgpt", "claude"];
const KNOWN_THEMES = ["light", "dark"];
// Below this width the MCP surface falls back to the base (mobile/immersive)
// layout, not the desktop card — see Experience.render(). Single source so the
// render gate and McpPlayer's surface-class cleanup can't drift.
export const MCP_DESKTOP_MIN_WIDTH = 600;

function params() {
  try {
    return new URLSearchParams(window.location.search);
  } catch {
    return new URLSearchParams("");
  }
}

export function isMcpAppSurface(): boolean {
  return params().get("pix_surface") === MCP_APP_SURFACE;
}

// True when the client has opted out of the desktop MCP card design (the shell
// reflects MCP_APP_NEW_DESIGN_ENABLED=false down as ?pix_mcp_layout=classic).
// Forces the base layout — at the shell's landscape card size that renders the
// original side-by-side split — while keeping pix_surface=mcp-app so link
// routing and analytics tagging still behave as the MCP embed.
export function isClassicMcpLayout(): boolean {
  return params().get("pix_mcp_layout") === MCP_LAYOUT_CLASSIC;
}

// True when the desktop MCP card layout (McpPlayer) should be showing: the MCP
// surface, the new design not disabled, AND a viewport at/above the desktop
// breakpoint. Otherwise the base layout renders instead, so the MCP surface
// classes must be cleaned up.
export function isMcpDesktopLayout(): boolean {
  try {
    return (
      isMcpAppSurface() &&
      !isClassicMcpLayout() &&
      typeof window !== "undefined" &&
      window.innerWidth > MCP_DESKTOP_MIN_WIDTH
    );
  } catch {
    return false;
  }
}

// True when the MCP surface is at/below the desktop breakpoint, so the base
// mobile layout renders in the card instead of McpPlayer (either design mode).
export function isMcpNarrowLayout(): boolean {
  try {
    return (
      isMcpAppSurface() &&
      typeof window !== "undefined" &&
      window.innerWidth <= MCP_DESKTOP_MIN_WIDTH
    );
  } catch {
    return false;
  }
}

// True when the MCP landscape/tabs layout should render: the MCP
// surface at/above the desktop breakpoint, on the classic layout value. This is
// the video-first landscape view with the Method/Ingredients/Equipment tabs
// UNDER the video (reusing McpTabs) instead of the old immersive LandscapeNav
// icons overlaid on the picture. Narrow MCP cards keep the base layout.
export function isMcpLandscapeLayout(): boolean {
  try {
    return (
      isMcpAppSurface() &&
      isClassicMcpLayout() &&
      typeof window !== "undefined" &&
      window.innerWidth > MCP_DESKTOP_MIN_WIDTH
    );
  } catch {
    return false;
  }
}

// Normalized host slug: 'chatgpt' | 'claude' | '' (unknown). Identity only —
// theming is derived from mcpThemeSlug(), not the host.
export function mcpHostSlug(): string {
  const raw = (params().get("pix_host") || "").toLowerCase();
  return KNOWN_HOSTS.indexOf(raw) === -1 ? "" : raw;
}

// The viewer's actual theme reported by the ChatGPT Apps SDK, if window.openai
// is present in THIS frame. 'light' | 'dark' | '' (absent/other host). Read
// defensively — window.openai only exists inside the host, and only for hosts
// that expose it.
export function mcpOpenAiTheme(): string {
  try {
    const t =
      window.openai && typeof window.openai.theme === "string"
        ? window.openai.theme.toLowerCase()
        : "";
    return KNOWN_THEMES.indexOf(t) !== -1 ? t : "";
  } catch {
    return "";
  }
}

// Resolved light/dark theme for the card: 'light' | 'dark' | '' (unknown → CSS
// follows prefers-color-scheme). Priority: ?pix_theme (the shell reports the
// host theme down to the nested player) → window.openai.theme read directly
// (when the host injects it into this frame) → '' so CSS uses the viewer's
// prefers-color-scheme. No hardcoded per-host default — that forced dark on a
// light ChatGPT before the real theme was known.
export function mcpThemeSlug(): string {
  const raw = (params().get("pix_theme") || "").toLowerCase();
  if (KNOWN_THEMES.indexOf(raw) !== -1) {
    return raw;
  }
  return mcpOpenAiTheme();
}
